import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  ClipboardList,
  ShoppingCart,
  Building2,
  MapPin,
  Bell,
  Truck,
  BarChart3,
  Settings,
  ChevronDown,
  ChevronRight,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  ChevronsUpDown,
} from 'lucide-react';

const NAV = [
  {
    section: 'Main',
    items: [{ path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    section: 'Sales',
    items: [
      { path: '/leads', label: 'Lead Management', icon: Users },
      { path: '/quotations', label: 'Quotations', icon: FileText },
      { path: '/proforma', label: 'Proforma Invoice', icon: ClipboardList },
      { path: '/orders', label: 'Orders', icon: ShoppingCart },
      { path: '/customers', label: 'Customers', icon: Building2 },
    ],
  },
  {
    section: 'Operations',
    items: [
      { path: '/visits', label: 'Visits', icon: MapPin },
      { path: '/followups', label: 'Follow-ups', icon: Bell, badge: 5 },
      { path: '/dispatch', label: 'Dispatch', icon: Truck },
    ],
  },
  {
    section: 'Insights',
    items: [
      {
        path: '/reports',
        label: 'Reports',
        icon: BarChart3,
        children: [
          { path: '/reports/dashboard', label: 'Dashboard' },
          { path: '/reports/leads', label: 'Lead Reports' },
          { path: '/reports/sales', label: 'Sales Reports' },
          { path: '/reports/quotations', label: 'Quotation Reports' },
          { path: '/reports/orders', label: 'Order Reports' },
          { path: '/reports/customers', label: 'Customer Reports' },
          { path: '/reports/products', label: 'Product Reports' },
          { path: '/reports/financial', label: 'Financial Reports' },
          { path: '/reports/territory', label: 'Territory Reports' },
          // { path: '/reports/performance', label: 'Performance' },
          // { path: '/reports/executive', label: 'Executive Dashboard' },
        ],
      },
    ],
  },
  {
    section: 'Administration',
    items: [
      {
        path: '/settings',
        label: 'Settings',
        icon: Settings,
        children: [
          { path: '/settings/users', label: 'User Management' },
          { path: '/settings/products', label: 'Product Master' },
          { path: '/settings/territory', label: 'Territory Management' },
        ],
      },
    ],
  },
];

export default function Sidebar({ collapsed, onToggleCollapse, mobileOpen, onCloseMobile }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState({});
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();

  const filteredNav = useMemo(() => {
    return NAV.map((section) => {
      const items = section.items.filter((item) => {
        if (!q) return true;
        const matchParent = item.label.toLowerCase().includes(q);
        const matchChild = item.children?.some((c) => c.label.toLowerCase().includes(q));
        return matchParent || matchChild;
      });
      return { ...section, items };
    }).filter((section) => section.items.length > 0);
  }, [q]);

  const toggleExpand = (path) => setExpanded((prev) => ({ ...prev, [path]: !prev[path] }));

  const isActive = (path) => location.pathname === path;
  const isParentActive = (item) =>
    item.children ? item.children.some((c) => location.pathname === c.path) : false;

  const handleItemClick = (item) => {
    if (collapsed) onToggleCollapse(false);
    if (item.children) {
      toggleExpand(item.path);
    } else {
      navigate(item.path);
      onCloseMobile();
    }
  };

  const handleChildClick = (path) => {
    navigate(path);
    onCloseMobile();
  };

  return (
    <>
      {mobileOpen && (
        <div
          onClick={onCloseMobile}
          className="fixed inset-0 bg-slate-900/40 z-40 lg:hidden"
        />
      )}

      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 flex flex-col bg-white border-r border-slate-200 transition-transform duration-200 ease-out lg:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          width: collapsed ? '76px' : '272px',
          transition: 'width 200ms ease-out, transform 200ms ease-out',
        }}
      >
        {/* Brand */}
        <div className="flex items-center gap-3 px-4 h-16 border-b border-slate-200 shrink-0">
          <div className="h-9 w-9 rounded-lg bg-indigo-600 flex items-center justify-center text-white font-semibold text-sm shrink-0">
            SS
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800 tracking-tight truncate">
                Nexora Sales System
              </p>
              <p className="text-xs text-slate-400 truncate">Tanzania</p>
            </div>
          )}
          <button
            onClick={onCloseMobile}
            className="ml-auto lg:hidden text-slate-400 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50 rounded-md p-1"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* Search */}
        {!collapsed && (
          <div className="px-3 pt-3 shrink-0">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search menu"
                className="w-full text-sm bg-slate-100 rounded-lg pl-8 pr-2 py-1.5 text-slate-600 placeholder-slate-400 outline-none focus:ring-2 focus:ring-indigo-500/40 focus:bg-white border border-transparent focus:border-indigo-200 transition-colors"
              />
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
          {filteredNav.map((section) => (
            <div key={section.section}>
              {!collapsed && (
                <p className="px-2.5 mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {section.section}
                </p>
              )}
              <ul className="space-y-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const hasChildren = !!item.children;
                  const active = isActive(item.path);
                  const parentActive = isParentActive(item);
                  const childMatches =
                    q && item.children?.some((c) => c.label.toLowerCase().includes(q));
                  const open = expanded[item.path] || childMatches || parentActive;

                  return (
                    <li key={item.path}>
                      <button
                        onClick={() => handleItemClick(item)}
                        title={collapsed ? item.label : undefined}
                        className={`group relative w-full flex items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50 ${
                          active || parentActive
                            ? 'bg-indigo-50 text-indigo-700'
                            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                        }`}
                      >
                        {(active || parentActive) && (
                          <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-full bg-indigo-600" />
                        )}
                        <Icon
                          size={17}
                          strokeWidth={2}
                          className={`shrink-0 ${
                            active || parentActive
                              ? 'text-indigo-600'
                              : 'text-slate-400 group-hover:text-slate-500'
                          }`}
                        />
                        {!collapsed && (
                          <span className="text-sm font-medium truncate flex-1">{item.label}</span>
                        )}
                        {!collapsed && item.badge && !hasChildren && (
                          <span className="text-xs font-semibold bg-rose-100 text-rose-600 rounded-full px-1.5 py-0.5">
                            {item.badge}
                          </span>
                        )}
                        {!collapsed && hasChildren && (
                          open ? (
                            <ChevronDown size={14} className="text-slate-400 shrink-0" />
                          ) : (
                            <ChevronRight size={14} className="text-slate-400 shrink-0" />
                          )
                        )}
                      </button>

                      {hasChildren && !collapsed && open && (
                        <ul className="mt-0.5 ml-8 border-l border-slate-200 pl-3 space-y-0.5">
                          {item.children.map((child) => (
                            <li key={child.path}>
                              <button
                                onClick={() => handleChildClick(child.path)}
                                className={`w-full text-left text-sm rounded-md px-2 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50 ${
                                  isActive(child.path)
                                    ? 'text-indigo-700 font-medium bg-indigo-50'
                                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                                }`}
                              >
                                {child.label}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          {filteredNav.length === 0 && (
            <p className="px-2.5 text-sm text-slate-400">No matches for &quot;{query}&quot;</p>
          )}
        </nav>

        {/* Collapse toggle (desktop only) */}
        <div className="hidden lg:flex justify-end px-2 py-2 border-t border-slate-100 shrink-0">
          <button
            onClick={() => onToggleCollapse(!collapsed)}
            className="p-1.5 rounded-md text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </div>

        {/* Footer / user */}
        <div className="border-t border-slate-200 p-3 shrink-0">
          <button className="w-full flex items-center gap-2.5 rounded-lg px-1.5 py-1.5 hover:bg-slate-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50">
            <div className="h-8 w-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-semibold shrink-0">
              JD
            </div>
            {!collapsed && (
              <>
                <div className="min-w-0 text-left flex-1">
                  <p className="text-sm font-medium text-slate-700 truncate">John Doe</p>
                  <p className="text-xs text-slate-400 truncate">Sales Executive</p>
                </div>
                <ChevronsUpDown size={14} className="text-slate-400 shrink-0" />
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  );
}